/**
 * Canonical 6-group task-oriented navigation.
 *
 * Replaces the prior 3-layout (classic/verb/timeline) experiment, which
 * the audit (NS3, 2026-05-10) flagged as cognitive-load-heavy and net-
 * neutral in value. The new shape is a single canonical organization
 * built around what the operator actually does in a session:
 *
 *   1. Today      — daily inputs (capture, hours, expenses, mileage,
 *                   chat, dashboard)
 *   2. Money      — flow of cash in/out (invoices, bills, contractors,
 *                   reconcile, payroll, pay periods)
 *   3. People     — humans + comp + projects (HR, comp review, creds,
 *                   projects)
 *   4. Compliance — laws/forms/audits/workflows (compliance calendar,
 *                   AI checklist, workflows, tax planning)
 *   5. Documents  — files + records (documents, contracts, continuity,
 *                   activity)
 *   6. Reports    — analytics + multi-entity rollups (reports,
 *                   consolidated — consolidated auto-hides for single-
 *                   entity tenants via whenMultiEntity flag)
 *
 * Manager portal items appear in their own pinned group above the main
 * nav for users with manager+ access (existing pattern preserved).
 *
 * Settings + Help + Sign Out remain bottom-pinned in SideNav itself.
 *
 * Each item's minRole MUST match the RoleGuard on its route in App.tsx.
 * Viewer-level users simply don't see gated items; groups with no
 * visible items auto-hide.
 *
 * The exported NAV_LAYOUT_META + NavLayoutKind types remain for back-
 * compat with the now-no-op layout picker (which logs a deprecation in
 * the Settings card; user's old localStorage value is silently cleaned
 * up on next load).
 */

import type { ComponentType } from 'react';
import {
  Home, Camera, Receipt, BarChart3, Shield, ShieldCheck, Wallet,
  Sparkles, Calculator, Activity, Car, FileText, DollarSign, Users,
  Clock, Award, UserCog, FileStack, Briefcase, LifeBuoy, FileSignature,
  Workflow, ClipboardCheck, Calendar, Layers, ListChecks,
  // Sidebar P2 (2026-05-21): split Tax from Compliance + add 5 new tax routes
  TrendingUp, Package, FileSpreadsheet, FileBadge,
  // Sprint 1.5 (2026-05-28): Settings drilldown group
  UserCircle, Bell, CreditCard, Gauge,
  // 2026-06-02: settings index completeness (personal tax + API keys had no
  // home outside the /settings page; now part of the canonical list).
  KeyRound,
  // Nav declutter (2026-06-15): distinct icons (kill the ShieldCheck/Wallet/
  // Calculator reuse).
  Scale, UserCheck, Banknote, PiggyBank,
  // SMS expense-capture opt-in (2026-06-19).
  MessageSquare,
  CalendarDays,
  // Scheduling Phase B-E (2026-07-14): team schedule / who's-free calendar.
  CalendarRange,
} from 'lucide-react';
import type { UserRole } from '@/types';

export interface NavItem {
  to: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  minRole?: UserRole;
  /**
   * If true, item only renders when the active user belongs to more
   * than one entity (i.e., a real organization with multiple LLCs).
   * Used for /consolidated which is degenerate for single-entity tenants.
   */
  whenMultiEntity?: boolean;
  /**
   * Plain-English hover hint (rendered as the row's title attribute).
   * Used to keep an IRS form number / term learnable after a label is
   * de-jargoned (nav declutter 2026-06-15) — e.g. label "Book-vs-tax",
   * hint "Schedule M-1 — book vs. tax income reconciliation".
   */
  hint?: string;
}

export interface NavGroup {
  key: string;
  label: string | null;
  items: NavItem[];
  defaultOpen: boolean;
  /**
   * 5-section consolidation (2026-06-18). When true, the group renders in the
   * sidebar ONLY for a pure-manager login (role === 'manager'); owners/admins
   * don't see it (their /workflows + /hr are the entity-wide supersets, and the
   * manager-scoped routes stay in TASK_LAYOUT so ⌘P still finds them). Keeps the
   * owner sidebar to 5 clean sections without losing any function.
   */
  managerOnly?: boolean;
}

// One canonical layout. The kind type stays for back-compat with the
// Settings card and any persisted user preference; it's no longer used
// to switch layouts, just kept so the type system doesn't need to
// rip out every reference.
export type NavLayoutKind = 'task';

export interface NavLayoutMeta {
  kind: NavLayoutKind;
  name: string;
  tagline: string;
  description: string;
}

export const NAV_LAYOUT_META: Record<NavLayoutKind, NavLayoutMeta> = {
  task: {
    kind: 'task',
    name: 'Default',
    tagline: 'Five task-oriented sections',
    description: 'Money · People · Compliance & tax · Documents · Reports. The canonical Nexus navigation; the "Daily" catch-all was folded into Money/People in the 2026-06-18 5-section consolidation.',
  },
};

// ---------------------------------------------------------------------------
// Item definitions — one source of truth, referenced from groups below.
// ---------------------------------------------------------------------------

// Sprint 1.5 (2026-05-28): "Dashboard" → "Home" — the route already hosts
// the Command Center (Sprint 1 rename); the sidebar label catches up.
const HOME: NavItem = { to: '/', icon: Home, label: 'Home' };
const ASK_NEXUS: NavItem = { to: '/query', icon: Sparkles, label: 'Ask Nexus' };
const CAPTURE: NavItem = { to: '/capture', icon: Camera, label: 'Smart Capture' };
const EXPENSES: NavItem = { to: '/expenses', icon: Receipt, label: 'Expenses' };
// Sidebar P1 (2026-05-20): "Days Worked" → "Time" (profession-neutral; the
// route still serves a physician logging shifts AND an attorney logging
// billable hours AND a realtor logging showings — calling it "Days Worked"
// telegraphs locums-physician bias even though the actual implementation
// has been preset-aware since Preset Phase 1).
const DAYS: NavItem = { to: '/hours', icon: Clock, label: 'Time' };
const WORK_CALENDAR: NavItem = { to: '/hours/calendar', icon: CalendarDays, label: 'Work calendar' };
const MILEAGE: NavItem = { to: '/mileage', icon: Car, label: 'Mileage' };

const INVOICES: NavItem = { to: '/invoices', icon: DollarSign, label: 'Invoices' };
const BILLS: NavItem = { to: '/finance/bills', icon: FileStack, label: 'Bills', hint: 'Bills you owe (accounts payable)' };
const CONTRACTORS: NavItem = { to: '/contractors', icon: Users, label: 'Contractors' };
const RECONCILE: NavItem = { to: '/finance/reconcile', icon: Wallet, label: 'Bank reconciliation', minRole: 'admin' };
// Distinct from bank reconciliation: matches a credit-card statement's charges
// to captured expenses (a different pipeline + tables).
const CARD_RECONCILE: NavItem = { to: '/finance/card-statements', icon: CreditCard, label: 'Card reconciliation', minRole: 'admin' };
// 2026-06-17: minRole was 'bookkeeper' but the /payroll RoleGuard is 'admin'
// (App.tsx) — bookkeepers saw the row then got blocked. Aligned to 'admin'.
const PAYROLL: NavItem = { to: '/payroll', icon: Banknote, label: 'Payroll', minRole: 'admin' };
const PAY_PERIODS: NavItem = { to: '/hr/pay-periods', icon: Calendar, label: 'Pay periods', minRole: 'admin' };

const HR: NavItem = { to: '/hr', icon: UserCog, label: 'HR', minRole: 'bookkeeper' };
const COMP: NavItem = { to: '/compensation-review', icon: Scale, label: 'Compensation review', minRole: 'admin', hint: 'Reasonable-compensation review (S-corp)' };
const CREDS: NavItem = { to: '/credentials', icon: Award, label: 'Credentials & CE', hint: 'Licenses, board certifications, and continuing education (CME/CLE/CPE) — with renewal deadlines' };
const PROJECTS: NavItem = { to: '/projects', icon: Briefcase, label: 'Projects' };

// Sidebar P1: "Compliance" → "Compliance calendar" so its sibling
// "Checklist (AI)" no longer looks like a redundant entry. The two answer
// different operator questions: calendar = WHEN (date-anchored
// deadlines), checklist = DOES THIS APPLY (profile-anchored
// applicability + has-it-been-done state).
const COMPLIANCE: NavItem = { to: '/compliance', icon: Shield, label: 'Compliance calendar' };
const CHECKLIST: NavItem = { to: '/compliance/checklist', icon: ListChecks, label: 'Checklist (AI)', minRole: 'accountant' };
const LOCUMS_CLASSIFICATIONS: NavItem = { to: '/locums/classifications', icon: UserCheck, label: 'Worker classification', minRole: 'viewer', hint: 'Employee vs. contractor (1099 vs. W-2) classification' };
const WORKFLOWS: NavItem = { to: '/workflows', icon: Workflow, label: 'Workflows', minRole: 'bookkeeper' };
// Sidebar P2 (2026-05-21): Tax becomes its own group split from
// Compliance. Tax Planning is the entry point; sub-routes follow.
// minRole on each item matches the RoleGuard in App.tsx exactly.
const TAX: NavItem = { to: '/tax', icon: Calculator, label: 'Tax Planning', minRole: 'accountant' };
const TAX_OPTIMIZER: NavItem = { to: '/tax/optimizer', icon: TrendingUp, label: 'Tax Optimizer', minRole: 'admin' };
const TAX_1099: NavItem = { to: '/tax/1099-input', icon: FileBadge, label: 'Contractor 1099s', minRole: 'accountant', hint: '1099-NEC/MISC amounts for contractors you paid' };
const TAX_FIXED_ASSETS: NavItem = { to: '/tax/fixed-assets', icon: Package, label: 'Capital purchases', minRole: 'bookkeeper', hint: 'Fixed-asset register — capital purchases, §179 + depreciation' };
const TAX_SCHEDULE_M1: NavItem = { to: '/tax/schedule-m1', icon: FileSpreadsheet, label: 'Book-vs-tax', minRole: 'bookkeeper', hint: 'Schedule M-1 — book vs. tax income reconciliation' };
const TAX_FORM_945: NavItem = { to: '/tax/form-945', icon: FileText, label: 'Withheld taxes (945)', minRole: 'viewer', hint: 'Form 945 — annual federal income tax withheld from non-payroll payments' };

const DOCS: NavItem = { to: '/documents', icon: FileText, label: 'Documents' };
const CONTRACTS: NavItem = { to: '/contracts', icon: FileSignature, label: 'Contracts' };
const CONTINUITY: NavItem = { to: '/continuity', icon: LifeBuoy, label: 'Continuity', minRole: 'owner', hint: 'Business continuity & succession plan — owner only' };
const ACTIVITY: NavItem = { to: '/activity', icon: Activity, label: 'Activity' };

const REPORTS: NavItem = { to: '/reports', icon: BarChart3, label: 'Reports', minRole: 'accountant' };
const CONSOLIDATED: NavItem = { to: '/consolidated', icon: Layers, label: 'Consolidated', minRole: 'admin', whenMultiEntity: true };

// Sprint 1.5 (2026-05-28): Settings drilldown — formerly a single sidebar
// link, now a 6-item drilldown. Per operator decision 2026-05-28 Q1=C:
// AI usage + Security & sessions are NEW route stubs; Preferences +
// Integrations dropped (the latter never had a real surface).
const SETTINGS_ACCOUNT: NavItem = { to: '/settings', icon: UserCircle, label: 'Account & profile' };
const SETTINGS_SECURITY: NavItem = { to: '/settings/security', icon: ShieldCheck, label: 'Security & sessions' };
// Scheduling Phase B-E (2026-07-14): personal Google/Apple calendar sync,
// used for conflict detection + the "who's free" capacity view.
const SETTINGS_CALENDAR_SYNC: NavItem = { to: '/settings/calendar-sync', icon: CalendarRange, label: 'Calendar sync', hint: 'Connect your personal calendar so Nexus can spot conflicts' };
const SETTINGS_NOTIFICATIONS: NavItem = { to: '/settings/notifications', icon: Bell, label: 'Notifications' };
const SETTINGS_SMS: NavItem = { to: '/settings/sms', icon: MessageSquare, label: 'Text capture (SMS)' };
// 2026-06-17: minRole 'admin' to match the /settings/ai-usage RoleGuard (lowered
// owner→admin same day so the admin-level AI-budget alert isn't a dead end; the
// backend GET /ai/usage already allows any entity member). Managing the cap /
// buying a top-up still routes to owner-only Billing.
const SETTINGS_AI_USAGE: NavItem = { to: '/settings/ai-usage', icon: Gauge, label: 'AI usage', minRole: 'admin' };
const SETTINGS_BILLING: NavItem = { to: '/billing', icon: CreditCard, label: 'Billing & subscription', minRole: 'owner' };
// 2026-06-02: Personal tax + API keys previously had NO home outside the
// /settings page itself (orphaned from the sidebar + avatar menu). Folded into
// the canonical list so every settings sub-page is reachable everywhere.
const SETTINGS_PERSONAL_TAX: NavItem = { to: '/settings/personal-tax', icon: PiggyBank, label: 'Personal tax', minRole: 'owner' };
const SETTINGS_API_KEYS: NavItem = { to: '/settings/api-keys', icon: KeyRound, label: 'API keys', minRole: 'admin' };
const SETTINGS_HELP: NavItem = { to: '/help', icon: LifeBuoy, label: 'Help & support' };

/**
 * CANONICAL settings sections — the single source of truth (2026-06-02). Used by
 * BOTH the sidebar/drawer Settings group AND the /settings page index, so the
 * two can never drift and every settings sub-page has a home in both places.
 * Order: Account first, Security second (most-needed), Help last.
 */
export const SETTINGS_SECTIONS: NavItem[] = [
  SETTINGS_ACCOUNT,
  SETTINGS_SECURITY,
  SETTINGS_CALENDAR_SYNC,
  SETTINGS_NOTIFICATIONS,
  SETTINGS_SMS,
  SETTINGS_AI_USAGE,
  SETTINGS_BILLING,
  SETTINGS_PERSONAL_TAX,
  SETTINGS_API_KEYS,
  SETTINGS_HELP,
];

// Manager Portal items (visible only to manager+ via minRole). Pinned
// in its own group near the top to give pure-manager users a clean
// single-purpose surface.
const MGR_APPROVALS: NavItem = { to: '/manager/approvals', icon: ClipboardCheck, label: 'Approvals', minRole: 'manager' };
const MGR_TEAM: NavItem = { to: '/manager/employees', icon: Users, label: 'My team', minRole: 'manager' };
const MGR_CARDHOLDER: NavItem = { to: '/manager/cardholder', icon: CreditCard, label: 'Card charges', minRole: 'manager' };  // Phase 9
// Scheduling Phase B-E (2026-07-14): "who's free" capacity view + the
// drag-free block-time calendar. Same route for a pure manager (via this
// item, gated minRole='manager' + the managerOnly group filter below) and
// for owner/admin (via SCHEDULE_TEAM in PEOPLE_GROUP, gated minRole='admin')
// — mirrors ManagerRouteGuard's own isManager||isAdmin condition exactly so
// neither role sees a nav link that then dead-ends on a permission screen.
const MGR_SCHEDULE: NavItem = { to: '/manager/schedule', icon: CalendarRange, label: 'Team schedule', minRole: 'manager', hint: "Who's free, conflicts, and blocking time off for your team" };

// ---------------------------------------------------------------------------
// Group definitions — one canonical 6-group layout (plus the always-pinned
// Home + Manager mini-groups at the top).
// ---------------------------------------------------------------------------

const HOME_GROUP: NavGroup = {
  key: 'home', label: null, defaultOpen: true,
  // Smart Capture is the universal "drop anything" front door — it classifies +
  // routes to Expenses/Credentials/Contractors/Tax/Insurance/Contracts/Documents,
  // so it's NOT money-specific. Promoted to top-level (2026-06-19) next to Home.
  items: [HOME, CAPTURE, ASK_NEXUS],
};

// 5-section consolidation (2026-06-18): managerOnly so owners/admins no longer
// see the manager portal in their sidebar (redundant with /workflows + /hr;
// still ⌘P-searchable). Pure managers keep it as their portal.
const MANAGER_GROUP: NavGroup = {
  key: 'manager',
  label: 'Manager',
  defaultOpen: true,
  managerOnly: true,
  items: [MGR_APPROVALS, MGR_TEAM, MGR_SCHEDULE, MGR_CARDHOLDER],
};

// 5-section consolidation (2026-06-18): the vague "Daily" catch-all is GONE.
// Its items fold into the sections they belong to — Expenses + Mileage are money
// actions → Money; Time (hours logged) is people/team → People. Smart Capture
// was promoted to top-level (2026-06-19) — it routes far beyond money.
// Nothing removed; every route stays in TASK_LAYOUT (so ⌘P finds it) + the sidebar.
const MONEY_GROUP: NavGroup = {
  key: 'money',
  label: 'Money',
  defaultOpen: true,
  items: [EXPENSES, MILEAGE, INVOICES, BILLS, CONTRACTORS, RECONCILE, CARD_RECONCILE, PAYROLL, PAY_PERIODS],
};

// Owner/admin path to the same /manager/schedule page MGR_SCHEDULE links to
// for a pure manager — see the comment on MGR_SCHEDULE above.
const SCHEDULE_TEAM: NavItem = { to: '/manager/schedule', icon: CalendarRange, label: 'Team schedule', minRole: 'admin', hint: "Who's free, conflicts, and blocking time off across the team" };

const PEOPLE_GROUP: NavGroup = {
  key: 'people',
  label: 'People',
  defaultOpen: false,
  items: [HR, DAYS, WORK_CALENDAR, SCHEDULE_TEAM, COMP, CREDS, PROJECTS],
};

// Nav declutter (2026-06-15): MERGED Compliance + Tax back into one group.
// The P2 (2026-05-21) split read as arbitrary to a non-CPA owner (peers —
// QuickBooks "Taxes", Xero "Tax" — keep it one), and two collapsed headers
// added scanning cost. One group, owner-facing items first, the rarer
// form-tools last. Default-collapsed.
// 5-section consolidation (2026-06-18): "Tax & compliance" → "Compliance & tax"
// (matches the approved mockup + reads compliance-first for a non-CPA owner —
// "what do I owe / when" before the deeper tax tools). Items reordered to put the
// owner-facing compliance surfaces first, tax planning next, form-tools last.
const TAX_COMPLIANCE_GROUP: NavGroup = {
  key: 'tax-compliance',
  label: 'Compliance & tax',
  defaultOpen: false,
  items: [
    COMPLIANCE, CHECKLIST, TAX, TAX_OPTIMIZER, WORKFLOWS, LOCUMS_CLASSIFICATIONS,
    TAX_1099, TAX_FIXED_ASSETS, TAX_SCHEDULE_M1, TAX_FORM_945,
  ],
};

const DOCUMENTS_GROUP: NavGroup = {
  key: 'documents',
  label: 'Documents',
  defaultOpen: false,
  items: [DOCS, CONTRACTS, CONTINUITY, ACTIVITY],
};

const REPORTS_GROUP: NavGroup = {
  key: 'reports',
  label: 'Reports',
  defaultOpen: false,
  items: [REPORTS, CONSOLIDATED],
};

// Sprint 1.5 (2026-05-28): Settings drilldown replaces the single
// /settings link that lived in the sidebar bottom row. Each item points
// at an existing or new-stub route; viewer+ can see all but Billing
// (owner only) — matches the RoleGuard on the corresponding routes in
// App.tsx.
const SETTINGS_GROUP: NavGroup = {
  key: 'settings',
  label: 'Settings',
  defaultOpen: false,
  items: SETTINGS_SECTIONS, // canonical list — shared with the /settings page index
};

// Canonical layout. Sidebar P2 (2026-05-21) split Compliance & tax →
// Compliance + Tax. Sprint 1.5 (2026-05-28) adds Settings drilldown as
// the 8th functional group + moves Admin OUT of the sidebar to the
// topbar avatar dropdown (UserDropdown.tsx). Order matters: Compliance
// before Tax because tax planning applies compliance rules; Settings
// last because it's configuration, touched least frequently.
// Nav declutter (2026-06-15): Compliance + Tax merged → one group (7 functional
// groups, down from 9). SETTINGS_GROUP stays in this list so every settings
// sub-page remains Cmd+P-searchable, but SideNav renders it as a single footer
// link (not a 10th sidebar group) — see SideNav.tsx.
// 5-section consolidation (2026-06-18): "Daily" group removed (its items folded
// into Money + People). Owner-visible sidebar sections = Money, People,
// Compliance & tax, Documents, Reports (the Manager group is managerOnly; Settings
// renders as a footer link). Every route remains in this list → ⌘P finds all.
export const TASK_LAYOUT: NavGroup[] = [
  HOME_GROUP,
  MANAGER_GROUP,
  MONEY_GROUP,
  PEOPLE_GROUP,
  TAX_COMPLIANCE_GROUP,
  DOCUMENTS_GROUP,
  REPORTS_GROUP,
  SETTINGS_GROUP,
];

// Legacy export: NAV_LAYOUTS used to be a kind→groups map. Keep the
// shape so no caller needs to change, but every kind resolves to the
// canonical TASK_LAYOUT.
export const NAV_LAYOUTS: Record<NavLayoutKind, NavGroup[]> = {
  task: TASK_LAYOUT,
};

const LAYOUT_STORAGE_KEY = 'nexus_nav_layout_kind';
const COLLAPSED_STORAGE_KEY = 'nexus_nav_collapsed_groups';

export function loadNavLayoutKind(): NavLayoutKind {
  // Back-compat: any prior value (classic/verb/timeline) silently maps
  // to the canonical layout. We don't rewrite the storage value so
  // nothing else has to know — the next saveNavLayoutKind() will land it.
  return 'task';
}

export function saveNavLayoutKind(_kind: NavLayoutKind) {
  // Single-layout system. We still persist the canonical key so the
  // localStorage doesn't drift if a future migration re-introduces
  // multiple layouts. Per-group collapse state is independent and is
  // cleared so a user upgrading from a prior layout sees the new
  // group defaults rather than a half-broken collapse map.
  try { localStorage.setItem(LAYOUT_STORAGE_KEY, 'task'); } catch { /* */ }
  try { localStorage.removeItem(COLLAPSED_STORAGE_KEY); } catch { /* */ }
}

export { COLLAPSED_STORAGE_KEY };
